import type { OrderDetails } from '@/entities/order/model'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { fetchOrders } from '@/entities/order/api'
import { getRealtimeAuthSession } from '@/features/auth/model'
import { connectWebSocketForRealtime, disconnectWebSocketForStatus } from '@/shared/api/api.websocket'

interface RealtimeState {
  realtimeOrders: OrderDetails[]
  realtimeWaitTime: { waitTimeAverage: number, numberOfOrders: number } | null
  useRealtime: boolean
  isConnected: boolean
  lastRealtimeEventTime: string
  lastStateUpdateTime: string
  shouldConnect: boolean
  isInitialized: boolean
  isInitialSyncComplete: boolean
  invalidateQueriesCallback: (() => void) | null
  invalidateStateUpdateCallback: (() => void) | null
  invalidateWaitTimeCallback: (() => void) | null
}

interface RealtimeStore extends RealtimeState {
  initialise: () => void
  setShouldConnect: (shouldConnect: boolean) => void
  updateConnectionStatus: (isConnected: boolean, lastEventTime?: string) => void
  handleOrderUpdate: (order: OrderDetails) => void
  handleWaitTimeUpdate: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => void
  handleStateUpdate: () => void
  syncOrders: (useRealtimeAfter?: boolean) => Promise<void>
  reset: () => void
  setInvalidateQueriesCallback: (callback: () => void) => void
  setInvalidateStateUpdateCallback: (callback: () => void) => void
  setInvalidateWaitTimeCallback: (callback: () => void) => void
}

const initialState: RealtimeState = {
  realtimeOrders: [],
  realtimeWaitTime: null,
  useRealtime: false,
  isConnected: false,
  lastRealtimeEventTime: '',
  lastStateUpdateTime: '',
  shouldConnect: false,
  isInitialized: false,
  isInitialSyncComplete: false,
  invalidateQueriesCallback: null,
  invalidateStateUpdateCallback: null,
  invalidateWaitTimeCallback: null,
}

/**
 * Update an order in the array. Adds new orders or updates existing ones if the new order
 * has a more recent lastUpdateTime. Returns a new array to maintain immutability.
 */
function updateOrderInArray(orders: OrderDetails[], newOrder: OrderDetails): OrderDetails[] {
  const newOrders = [...orders]
  const existingOrderIndex = newOrders.findIndex(
    existingOrder => existingOrder.orderId === newOrder.orderId,
  )

  if (existingOrderIndex === -1) {
    newOrders.push(newOrder)
  } else if (newOrders[existingOrderIndex].lastUpdateTime < newOrder.lastUpdateTime) {
    newOrders[existingOrderIndex] = newOrder
  }

  return newOrders
}

export const useRealtimeStore = create<RealtimeStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    /**
     * Initialize the realtime store
     * Sets up WebSocket connection monitoring
     */
    initialise: () => {
      if (get().isInitialized) {
        return
      }

      set({ isInitialized: true })
    },

    /**
     * Set whether realtime should be connected
     * Manages WebSocket connection lifecycle
     */
    setShouldConnect: (shouldConnect: boolean) => {
      const currentShouldConnect = get().shouldConnect

      if (shouldConnect === currentShouldConnect) {
        return
      }

      set({ shouldConnect })

      if (shouldConnect && !currentShouldConnect) {
        const session = getRealtimeAuthSession()

        if (session) {
          connectWebSocketForRealtime(
            session.region,
            session.token,
            get().handleOrderUpdate,
            get().handleStateUpdate,
            get().handleWaitTimeUpdate,
            () => get().syncOrders(true),
          )
        }
      } else if (!shouldConnect && currentShouldConnect) {
        disconnectWebSocketForStatus()
        set({ useRealtime: false })

        // Invalidate state update query to restart polling when switching from realtime to polling
        const { invalidateStateUpdateCallback } = get()
        if (invalidateStateUpdateCallback) {
          invalidateStateUpdateCallback()
        }
      }
    },

    /**
     * Update connection status from WebSocket monitor
     */
    updateConnectionStatus: (isConnected: boolean, lastEventTime?: string) => {
      set(state => ({
        isConnected,
        lastRealtimeEventTime: lastEventTime || state.lastRealtimeEventTime,
      }))
    },

    /**
     * Handle order updates from WebSocket
     */
    handleOrderUpdate: (order: OrderDetails) => {
      set((state) => {
        const newOrders = updateOrderInArray(state.realtimeOrders, order)

        return {
          realtimeOrders: newOrders,
          lastRealtimeEventTime: new Date().toISOString(),
        }
      })

      // Invalidate wait time queries if order contains wait time data
      if (order.waitTime !== undefined) {
        const { invalidateWaitTimeCallback } = get()
        if (invalidateWaitTimeCallback) {
          invalidateWaitTimeCallback()
        }
      }
    },

    /**
     * Handle wait time updates from WebSocket
     */
    handleWaitTimeUpdate: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => {
      set({
        realtimeWaitTime: waitTimeData,
        useRealtime: true,
        lastRealtimeEventTime: new Date().toISOString(),
      })
    },

    /**
     * Handle state updates from WebSocket
     * This will be used by components to trigger external callbacks
     */
    handleStateUpdate: () => {
      set({ lastStateUpdateTime: new Date().toISOString() })

      const { invalidateStateUpdateCallback } = get()
      if (invalidateStateUpdateCallback) {
        invalidateStateUpdateCallback()
      }
    },

    /**
     * Fetch orders via HTTP and update store. Used for both immediate sync on mount
     * (useRealtimeAfter=false) and WebSocket initial sync (useRealtimeAfter=true).
     * The useRealtimeAfter parameter controls whether to switch to realtime mode after sync.
     */
    syncOrders: async (useRealtimeAfter: boolean = false) => {
      if (get().isInitialSyncComplete && !useRealtimeAfter) {
        return
      }

      try {
        const freshOrders = await fetchOrders()
        set({
          realtimeOrders: freshOrders,
          useRealtime: useRealtimeAfter,
          lastRealtimeEventTime: new Date().toISOString(),
          isInitialSyncComplete: true,
        })
      } catch (error) {
        console.error('[RealtimeStore] Order sync failed:', error)
        // Fallback: enable realtime for new updates even if sync fails
        if (useRealtimeAfter) {
          set({
            useRealtime: true,
            lastRealtimeEventTime: new Date().toISOString(),
          })
        }
        set({
          isInitialSyncComplete: true,
        })
      }
    },

    /**
     * Reset the realtime state
     */
    reset: () => {
      set(initialState)
    },

    /**
     * Set callback to invalidate queries when switching from realtime to polling
     */
    setInvalidateQueriesCallback: (callback: () => void) => {
      set({ invalidateQueriesCallback: callback })
    },

    /**
     * Set callback to invalidate state update query when STATE_UPDATE is received or when realtime is disabled
     */
    setInvalidateStateUpdateCallback: (callback: () => void) => {
      set({ invalidateStateUpdateCallback: callback })
    },

    /**
     * Set callback to invalidate wait time queries when orders with waitTime are received
     */
    setInvalidateWaitTimeCallback: (callback: () => void) => {
      set({ invalidateWaitTimeCallback: callback })
    },
  })),
)

/**
 * Service for managing realtime state outside of React components
 */
export const realtimeService = {
  initialize: () => useRealtimeStore.getState().initialise(),
  setShouldConnect: (shouldConnect: boolean) => useRealtimeStore.getState().setShouldConnect(shouldConnect),
  getState: () => useRealtimeStore.getState(),
  reset: () => useRealtimeStore.getState().reset(),
}

// Expose realtime store on window for e2e tests and debugging
if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
  ;(window as any).useRealtimeStore = useRealtimeStore
}
